// Reading the JSON Schema a control is built from (spec/13 §13: "its JSON Schema (the control is
// built from it)").
//
// Two producers write these schemas and the reader has to take both: the registry, through Zod's
// `toJSONSchema` (`GET …/settings` returns one per setting), and the contract's OpenAPI document,
// for the venue record (vite/contract-schemas.ts). They agree on JSON Schema 2020-12 and differ in
// how they say "or null" — `anyOf: [X, {type: 'null'}]`, `type: ['string', 'null']`, or
// `allOf: [{$ref: X}, {type: ['object', 'null']}]` — so `resolveSchema` folds all three into one
// `nullable` flag. Only the subset a settings screen needs is understood; anything else becomes a
// JSON editor rather than a control that silently drops part of a value.

import { isMoneySchema, type SettingMoney } from './money';

export type JsonSchema = Readonly<Record<string, unknown>>;

export interface SchemaContext {
  /** `$defs` the schema's `#/$defs/…` references point into. */
  readonly defs: Readonly<Record<string, JsonSchema>>;
  /**
   * Extra keywords merged into a named definition as it is resolved — a screen's hints about
   * presentation, never about validity: `{ LocalTime: { format: 'time' } }` so a time of day gets a
   * time picker, `{ Timezone: { enum: [...] } }` so a timezone is chosen rather than typed.
   */
  readonly hints?: Readonly<Record<string, JsonSchema>>;
  /** The country a new address starts in: the brand configuration's default, else the launch market's. */
  readonly defaultCountry?: string;
  /**
   * The screen's currency and the formatter's words for an amount, for a money value
   * (`x-clavian-unit: money`, spec/03 §9): shown and typed in rupees, stored in paise. Without it — a
   * form with no settings view behind it — a money value is drawn as the plain integer it is.
   */
  readonly money?: SettingMoney;
  /** The venue's timezone, where the screen has one: opening hours say the times are in it. */
  readonly timezone?: string;
}

export interface ResolvedSchema {
  readonly schema: JsonSchema;
  readonly nullable: boolean;
}

export type ControlKind =
  | 'text'
  | 'email'
  | 'url'
  | 'time'
  | 'date'
  | 'number'
  | 'money'
  | 'checkbox'
  | 'choice'
  | 'multi-choice'
  | 'country'
  | 'time-ranges'
  | 'week'
  | 'hours-row'
  | 'object'
  | 'list'
  | 'variant'
  | 'constant'
  | 'custom'
  | 'json';

/** The custom control a schema asks for, e.g. `photos` (the venue's uploads). */
export const CUSTOM_CONTROL = 'x-clavian-control';

/**
 * A presentation hint on a pair of times: equal start and end mean the whole day, as they do for
 * a venue's opening interval (spec/13 §13) and not for a pass window, where they mean nothing.
 */
export const ALL_DAY_WHEN_EQUAL = 'x-clavian-all-day-when-equal';

const isRecord = (value: unknown): value is JsonSchema =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const typesOf = (schema: JsonSchema): readonly string[] => {
  const type = schema['type'];
  if (typeof type === 'string') return [type];
  return Array.isArray(type) ? type.filter((entry): entry is string => typeof entry === 'string') : [];
};

/** A member that only widens the type to include null: `{ type: 'null' }` or `{ type: ['object', 'null'] }`. */
const isNullWidening = (member: JsonSchema): boolean =>
  typesOf(member).includes('null') && Object.keys(member).every((key) => key === 'type' || key === 'description');

function refName(ref: string): string | undefined {
  const match = /^#\/(?:\$defs|definitions)\/(.+)$/.exec(ref);
  return match?.[1];
}

/**
 * Follow `$ref`s, fold `allOf` and the three spellings of "or null", and merge a screen's hints.
 * The result is one schema with no reference at its top level, and whether null is allowed.
 */
export function resolveSchema(input: JsonSchema, context: SchemaContext, depth = 0): ResolvedSchema {
  if (depth > 32) throw new Error('resolveSchema: the schema refers to itself too deeply.');
  let nullable = false;
  let schema: JsonSchema = input;

  const ref = schema['$ref'];
  if (typeof ref === 'string') {
    const name = refName(ref);
    const target = name === undefined ? undefined : context.defs[name];
    if (name === undefined || target === undefined) return { schema: {}, nullable: false };
    const { $ref: _ignored, ...siblings } = schema;
    const inner = resolveSchema(target, context, depth + 1);
    nullable ||= inner.nullable;
    schema = { ...inner.schema, ...(context.hints?.[name] ?? {}), ...siblings };
  }

  const allOf = schema['allOf'];
  if (Array.isArray(allOf)) {
    const { allOf: _ignored, ...rest } = schema;
    let merged: JsonSchema = {};
    for (const member of allOf.filter(isRecord)) {
      if (isNullWidening(member)) {
        nullable = true;
        continue;
      }
      const inner = resolveSchema(member, context, depth + 1);
      nullable ||= inner.nullable;
      merged = { ...merged, ...inner.schema };
    }
    schema = { ...merged, ...rest };
  }

  for (const keyword of ['anyOf', 'oneOf'] as const) {
    const members = schema[keyword];
    if (!Array.isArray(members)) continue;
    const records = members.filter(isRecord);
    const nonNull = records.filter((member) => !(typesOf(member).length === 1 && typesOf(member)[0] === 'null'));
    if (nonNull.length < records.length) nullable = true;
    if (nonNull.length === 1 && nonNull[0] !== undefined) {
      const { [keyword]: _ignored, ...rest } = schema;
      const inner = resolveSchema(nonNull[0], context, depth + 1);
      nullable ||= inner.nullable;
      schema = { ...inner.schema, ...rest };
    } else if (nonNull.length !== records.length) {
      schema = { ...schema, [keyword]: nonNull };
    }
  }

  const types = typesOf(schema);
  if (types.includes('null')) {
    nullable = true;
    const others = types.filter((type) => type !== 'null');
    schema = { ...schema, type: others.length === 1 ? others[0] : others };
  }

  return { schema, nullable };
}

/** The options of a `oneOf`/`anyOf`, resolved, when every one is an object — a union of shapes. */
export function variantsOf(schema: JsonSchema, context: SchemaContext): readonly JsonSchema[] {
  const members = schema['oneOf'] ?? schema['anyOf'];
  if (!Array.isArray(members)) return [];
  return members.filter(isRecord).map((member) => resolveSchema(member, context).schema);
}

/** The property every variant fixes with a `const` — the tag that says which shape a value is. */
export function discriminatorOf(variants: readonly JsonSchema[]): string | undefined {
  const first = variants[0];
  if (first === undefined) return undefined;
  const candidates = Object.keys(propertiesOf(first));
  return candidates.find((name) =>
    variants.every((variant) => {
      const property = propertiesOf(variant)[name];
      return isRecord(property) && 'const' in property;
    }),
  );
}

export function propertiesOf(schema: JsonSchema): Readonly<Record<string, JsonSchema>> {
  const properties = schema['properties'];
  if (!isRecord(properties)) return {};
  return Object.fromEntries(Object.entries(properties).filter((entry): entry is [string, JsonSchema] => isRecord(entry[1])));
}

export function requiredOf(schema: JsonSchema): ReadonlySet<string> {
  const required = schema['required'];
  return new Set(Array.isArray(required) ? required.filter((name): name is string => typeof name === 'string') : []);
}

export function enumOf(schema: JsonSchema): readonly (string | number | boolean)[] | undefined {
  const values = schema['enum'] ?? constsOf(schema);
  if (!Array.isArray(values)) return undefined;
  const scalars = values.filter(
    (value): value is string | number | boolean => ['string', 'number', 'boolean'].includes(typeof value),
  );
  return scalars.length === values.length ? scalars : undefined;
}

/**
 * The values of a union of constants — what zod writes for `z.union([z.literal(60), z.literal(300)])`: an
 * `anyOf` (or `oneOf`) whose every member is a bare `{ const }`. Such a setting is a pick-one-of like an
 * `enum`, so it gets the same radio or select rather than falling back to the JSON editor.
 */
function constsOf(schema: JsonSchema): unknown[] | undefined {
  const members = schema['anyOf'] ?? schema['oneOf'];
  if (!Array.isArray(members) || members.length === 0) return undefined;
  const values: unknown[] = [];
  for (const member of members as unknown[]) {
    if (typeof member !== 'object' || member === null || !('const' in member)) return undefined;
    values.push((member as { const: unknown }).const);
  }
  return values;
}

// ---------------------------------------------------------------------------------------------
// Shapes the renderer draws with a purpose-made control, recognised by their structure — so a
// registry setting of the same shape gets the same control with nothing but its schema.
// ---------------------------------------------------------------------------------------------

/** Accepts exactly the 24-hour `HH:MM` times: what the contract's `LocalTime` is. */
function patternIsTimeOfDay(pattern: string): boolean {
  try {
    const regex = new RegExp(pattern);
    return ['00:00', '09:30', '23:59'].every((time) => regex.test(time)) && !['24:00', '9:30', '12:60', 'noon'].some((time) => regex.test(time));
  } catch {
    return false;
  }
}

/** A time of day, `HH:MM`: by its format, or by a pattern that accepts exactly that. */
export function isTimeOfDay(schema: JsonSchema): boolean {
  if (typesOf(schema)[0] !== 'string') return false;
  return schema['format'] === 'time' || (typeof schema['pattern'] === 'string' && patternIsTimeOfDay(schema['pattern']));
}

/** An ISO 3166-1 alpha-2 country code: the contract's `CountryCode`, `^[A-Z]{2}$`. */
export function isCountryCode(schema: JsonSchema): boolean {
  return typesOf(schema)[0] === 'string' && schema['pattern'] === '^[A-Z]{2}$';
}

export interface RangeShape {
  /** The property that starts a range, and the one that ends it, in the schema's order. */
  readonly start: string;
  readonly end: string;
  readonly allDayWhenEqual: boolean;
}

/** An object that is exactly two required times of day: a range, `opens`–`closes`. */
export function rangeShapeOf(input: JsonSchema, context: SchemaContext): RangeShape | undefined {
  const { schema } = resolveSchema(input, context);
  if (typesOf(schema)[0] !== 'object') return undefined;
  const names = Object.keys(propertiesOf(schema));
  const required = requiredOf(schema);
  if (names.length !== 2 || !names.every((name) => required.has(name))) return undefined;
  const properties = propertiesOf(schema);
  if (!names.every((name) => isTimeOfDay(resolveSchema(properties[name] as JsonSchema, context).schema))) return undefined;
  return { start: names[0] as string, end: names[1] as string, allDayWhenEqual: schema[ALL_DAY_WHEN_EQUAL] === true };
}

/** A list of ranges: the hours of one day. */
export function rangesItemOf(input: JsonSchema, context: SchemaContext): RangeShape | undefined {
  const { schema } = resolveSchema(input, context);
  const items = schema['items'];
  if (typesOf(schema)[0] !== 'array' || !isRecord(items)) return undefined;
  return rangeShapeOf(items, context);
}

/** An object whose every property is the hours of one day: a week. Its property names, in order. */
export function weekDaysOf(schema: JsonSchema, context: SchemaContext): readonly string[] | undefined {
  if (typesOf(schema)[0] !== 'object') return undefined;
  const properties = propertiesOf(schema);
  const names = Object.keys(properties);
  if (names.length < 2) return undefined;
  return names.every((name) => rangesItemOf(properties[name] as JsonSchema, context) !== undefined) ? names : undefined;
}

export interface HoursRowShape {
  /** The one flag — `closed` on a holiday — and the hours it switches off. */
  readonly flag: string;
  readonly ranges: string;
  /** The flag means "shut" (its name says closed), so turning it on clears and hides the hours. */
  readonly flagCloses: boolean;
  /** Every other property, drawn beside them: a holiday's date and name. */
  readonly others: readonly string[];
}

/** An object with exactly one flag and exactly one list of ranges: one day's hours, with a switch. */
export function hoursRowOf(schema: JsonSchema, context: SchemaContext): HoursRowShape | undefined {
  if (typesOf(schema)[0] !== 'object') return undefined;
  const properties = propertiesOf(schema);
  const names = Object.keys(properties);
  const flags = names.filter((name) => typesOf(resolveSchema(properties[name] as JsonSchema, context).schema)[0] === 'boolean');
  const ranges = names.filter((name) => rangesItemOf(properties[name] as JsonSchema, context) !== undefined);
  if (flags.length !== 1 || ranges.length !== 1) return undefined;
  const flag = flags[0] as string;
  const list = ranges[0] as string;
  return {
    flag,
    ranges: list,
    flagCloses: /closed|shut/i.test(flag),
    others: names.filter((name) => name !== flag && name !== list),
  };
}

/**
 * How wide a field is inside an object: `short` fields share a row three to a line on a wide screen
 * (a city, a state and a PIN code), `medium` ones two, `wide` ones take the row. All stack on a
 * narrow screen.
 */
export function fieldWidth(input: JsonSchema, context: SchemaContext): 'short' | 'medium' | 'wide' {
  const { schema } = resolveSchema(input, context);
  const kind = controlKind(schema, context);
  switch (kind) {
    case 'number':
    case 'money':
    case 'time':
    case 'date':
    case 'country':
    case 'choice':
      return enumOf(schema) !== undefined && (enumOf(schema)?.length ?? 0) <= 4 ? 'wide' : 'short';
    case 'email':
      return 'medium';
    case 'text': {
      const pattern = schema['pattern'];
      if (typeof pattern === 'string' && pattern.startsWith('^\\+')) return 'short';
      // A city or a PIN code is short; an address line is not.
      const maxLength = schema['maxLength'];
      return typeof maxLength === 'number' && maxLength <= 80 ? 'short' : 'wide';
    }
    default:
      return 'wide';
  }
}

/** Which control a resolved schema gets. */
export function controlKind(schema: JsonSchema, context: SchemaContext): ControlKind {
  if (typeof schema[CUSTOM_CONTROL] === 'string') return 'custom';
  if ('const' in schema) return 'constant';
  if (enumOf(schema) !== undefined) return 'choice';
  if (isCountryCode(schema)) return 'country';
  if (context.money !== undefined && isMoneySchema(schema)) return 'money';

  const variants = variantsOf(schema, context);
  if (variants.length > 0) {
    return variants.every((variant) => typesOf(variant).includes('object')) && discriminatorOf(variants) !== undefined
      ? 'variant'
      : 'json';
  }

  const types = typesOf(schema);
  if (types.length !== 1) return 'json';
  switch (types[0]) {
    case 'boolean':
      return 'checkbox';
    case 'integer':
    case 'number':
      return 'number';
    case 'string': {
      const format = schema['format'];
      if (format === 'date') return 'date';
      if (isTimeOfDay(schema)) return 'time';
      if (format === 'email') return 'email';
      if (format === 'uri' || format === 'url') return 'url';
      return 'text';
    }
    case 'object':
      if (weekDaysOf(schema, context) !== undefined) return 'week';
      if (hoursRowOf(schema, context) !== undefined) return 'hours-row';
      return Object.keys(propertiesOf(schema)).length > 0 ? 'object' : 'json';
    case 'array': {
      const items = schema['items'];
      if (!isRecord(items)) return 'json';
      // A list of choices is a set of checkboxes; a list of time ranges is one day's hours; a list
      // of anything else is rows to add and remove.
      if (enumOf(resolveSchema(items, context).schema) !== undefined) return 'multi-choice';
      if (rangeShapeOf(items, context) !== undefined) return 'time-ranges';
      return 'list';
    }
    default:
      return 'json';
  }
}

/** A sensible empty value for a new list item or a newly added object: what the schema requires, blank. */
export function emptyValue(input: JsonSchema, context: SchemaContext): unknown {
  const { schema } = resolveSchema(input, context);
  if ('default' in schema) return schema['default'];
  if ('const' in schema) return schema['const'];
  if (isCountryCode(schema)) return context.defaultCountry ?? '';
  const choices = enumOf(schema);
  if (choices !== undefined) return choices[0];
  const variants = variantsOf(schema, context);
  if (variants[0] !== undefined) return emptyValue(variants[0], context);
  switch (typesOf(schema)[0]) {
    case 'boolean':
      return false;
    case 'integer':
    case 'number': {
      // Zero when the range allows it (a latitude starts at 0, not at -90), else the least allowed.
      const minimum = schema['minimum'];
      return typeof minimum === 'number' && minimum > 0 ? minimum : 0;
    }
    case 'string':
      return '';
    case 'array':
      return [];
    case 'object': {
      const required = requiredOf(schema);
      const value: Record<string, unknown> = {};
      for (const [name, property] of Object.entries(propertiesOf(schema))) {
        if (required.has(name)) value[name] = emptyValue(property, context);
      }
      return value;
    }
    default:
      return null;
  }
}
